import { useQuery } from '@tanstack/react-query'
import { useUser } from '@clerk/clerk-react'
import { api, buildQueryString } from '@/lib/api'
import type { AssetsResponse } from '@/types'

interface ActiveGenerationsResult {
  hasActiveGeneration: boolean
  activeCount: number
  isLoading: boolean
}

/**
 * Hook to check if user has any active (pending/processing) generations.
 * Used to enforce concurrent generation limits for non-Pro users.
 */
export function useActiveGenerations(): ActiveGenerationsResult {
  const { user, isLoaded } = useUser()

  const { data, isLoading } = useQuery({
    queryKey: ['activeGenerations', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('No user ID')
      // Fetch recent assets to check for processing/pending status
      const queryString = buildQueryString({
        page: 1,
        per_page: 10,
        source: 'generation',
      })
      return api.get<AssetsResponse>(`/asset/${user.id}${queryString}`)
    },
    enabled: isLoaded && !!user?.id,
    staleTime: 5 * 1000, // Consider data fresh for 5 seconds
    refetchInterval: 10 * 1000, // Refetch every 10 seconds to stay updated
  })

  // Count assets that are still being processed
  const activeGenerations = data?.assets?.filter(
    (asset) => asset.status === 'pending' || asset.status === 'processing'
  ) ?? []

  return {
    hasActiveGeneration: activeGenerations.length > 0,
    activeCount: activeGenerations.length,
    isLoading: !isLoaded || isLoading,
  }
}
